import { FontIcon } from '@fluentui/react';
import { Button, Dialog, Flex, LinkIcon, MenuButton, MoreIcon, Text } from '@fluentui/react-northstar';
import { Communication } from '@fluentui/react-teams';
import { Providers } from '@microsoft/mgt-element/dist/es6/providers/Providers';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import { Helmet } from "react-helmet";
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import ArchiveIcon from '../../svg/archive-icon.svg';
import UnArchiveIcon from '../../svg/Un-archive.svg';
import { MobileSettingsView } from '../automation/AutomationTable';
import { KBListWrapper } from '../kb/KBListWrapper';
import useCurrentPage from '../kb/useCurrentPage';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { Breadcrumbs } from '../shared/components/BreadcrumbNavigation';
import { CheckLogin } from '../shared/components/CheckLogin';
import { CheckLicense } from '../shared/components/License/CheckLicense';
import { LoadingScreen } from '../shared/components/LoadingScreen';
import { UpgradeNeeded } from '../shared/components/UpgradeNeeded';
import { TicketTemplate } from '../shared/interfaces/ticket.interface';
import { platformService } from '../shared/services/platform.service';
import {
  ARCHIVE_CONFIRMATION_WARNING,
  ARCHIVE_MODAL_HEADER,
  BUTTONS,
  CONTACT_ADMIN,
  DEFAULT_API_STATUS,
  DONE,
  ERROR,
  GETTING_ALL_TEMPLATES,
  LICENSE_FEATURE,
  LOADING,
  NEW_TEMPLATE,
  REFRESH_SESSION,
  SETTINGS,
  SETTINGS_PAGES,
  SOMETHING_WENT_WRONG,
  TEMPLATE_HEADERS,
  TEMPLATES,
  toastDefault,
  TREE_LISTING_CONSTANTS,
  UNARCHIVE_CONFIRMATION_SUBTITLE,
  UNARCHIVE_MODAL_HEADER,
  UNAUTHORIZED_ACCESS
} from '../shared/utils/constants';
import { toTitleCase } from '../shared/utils/helper';
import Style from './template.module.css';
import { TicketTemplatesDialog } from './TicketTemplatesDialog';
import { ScopePermissionPeopleComponent } from '../Rbac/ScopePermissionPeopleComponent';
import {
  getSortableTextValue,
  TABLE_SORT_META,
  TableSortConfig,
} from '../shared/components/table/tableSortConfig';

export const SortWithDefaultTemplateFirst = (queryResult: any[]) => {
  let templates: TicketTemplate[] = queryResult;
  const newTicketTemplate = templates.find(t => t.IsDefault);
  templates = templates.filter(t => !t.IsDefault);
  if (newTicketTemplate!=undefined)
    templates = [newTicketTemplate].concat(templates);
  return templates;
}

// The budget the Shared To cell collapses its groups to fit. Narrower than the
// service catalog's ceiling because this table carries eleven columns and the
// pixels are worth more to its neighbours here. Keep in step with the pinned
// width in template.module.css.
const SHARED_TO_COLUMN_WIDTH = 180;

export const TicketTemplatesTable = () => {
  const {t} = useTranslation();
  const history = useHistory();
  const api = new platformService();
  const [data, setData] = useState<RowsData[]>([]);
  const [apiStatus, setApiStatus] = useState<APIStatus>(
    DEFAULT_API_STATUS as APIStatus
  );
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TicketTemplate>({} as TicketTemplate);
  const {currentPage, setCurrentPage} = useCurrentPage()
  const [isFeatureEnabled, setIsFeatureEnabled] = useState(true);
  const templatesSortMeta = {
    title: '__sortTemplateTitle',
    ticketCount: '__sortTemplateTicketCount',
    assigneeSort: '__sortTemplateAssignee',
    teamSort: '__sortTemplateTeam',
    groupSort: '__sortTemplateGroup',
    customFormExistsSort: '__sortTemplateCustomFormExists',
    lifecycleSort: '__sortTemplateLifecycle',
    modifiedDateIso: '__sortTemplateModifiedDateIso',
  } as const;

  const ticketTemplatesSortConfig = useMemo((): TableSortConfig<string> => {
    const nameKey = t('template.template-headers.name');
    const ticketCountKey = t('template.template-headers.ticket-count');
    const createdDateKey = t('template.template-headers.created-date');
    const assigneeKey = t('template.fields.assignee');
    const teamKey = t('template.fields.team');
    const groupKey = t('template.fields.group');
    const customFormExistsKey = t('template.template-headers.custom-forms-exist');
    const lifecycleKey = t('template.fields.lifecycle');
    const modifiedDateKey = t('template.template-headers.modified-date');
    return {
      storageKey: 'settingsTicketTemplatesTableSort',
      defaultSort: { sortField: 'createdDate', sortDirection: 'desc' },
      columns: [
        {
          field: 'name',
          label: nameKey,
          headerKey: nameKey,
          getSortValue: (row) => getSortableTextValue(row[templatesSortMeta.title]),
        },
        {
          field: 'ticketCount',
          label: ticketCountKey,
          headerKey: ticketCountKey,
          getSortValue: (row) => Number(row[templatesSortMeta.ticketCount] ?? 0),
        },
        {
          field: 'team',
          label: teamKey,
          headerKey: teamKey,
          getSortValue: (row) => getSortableTextValue(row[templatesSortMeta.teamSort]),
        },
        {
          field: 'group',
          label: groupKey,
          headerKey: groupKey,
          getSortValue: (row) => getSortableTextValue(row[templatesSortMeta.groupSort]),
        },
        {
          field: 'assignee',
          label: assigneeKey,
          headerKey: assigneeKey,
          getSortValue: (row) => getSortableTextValue(row[templatesSortMeta.assigneeSort]),
        },
        {
          field: 'customFormExists',
          label: customFormExistsKey,
          headerKey: customFormExistsKey,
          getSortValue: (row) => Number(row[templatesSortMeta.customFormExistsSort] ?? 0),
        },
        {
          field: 'lifecycle',
          label: lifecycleKey,
          headerKey: lifecycleKey,
          getSortValue: (row) => getSortableTextValue(row[templatesSortMeta.lifecycleSort]),
        },
        {
          field: 'createdDate',
          label: createdDateKey,
          headerKey: createdDateKey,
          getSortValue: (row) => getSortableTextValue(row[TABLE_SORT_META.createdDateIso]),
        },
        {
          field: 'modifiedDate',
          label: modifiedDateKey,
          headerKey: modifiedDateKey,
          getSortValue: (row) => getSortableTextValue(row[templatesSortMeta.modifiedDateIso]),
        },
      ],
    };
  }, [t]);


  const getTemplateTicketCount = (id: number, ticketCount: any[]) => {
    const count = ticketCount.find((x) => x.TemplateId == id);
    return count?.Count ?? 0;
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [scopingEnabled, setScopingEnabled] = useState(false);
  
  const fetchData = useCallback(async () => {
    const isArchived = localStorage.getItem('isArchivedTicketTemplatesView') === 'true'
    setApiStatus({ ...apiStatus, msg: GETTING_ALL_TEMPLATES });
    try {
      const result = await Promise.all([
        getCachedFeature(LICENSE_FEATURE.TicketTemplates),
        api.getTicketTemplates(
          '?$orderby=Name asc&$expand=ScopedMembers,Team($select=Name),Assignee($select=FullName),SupportGroup($select=Name),Lifecycle($select=Title)' +
            (isArchived ? '' : ' &$filter=IsArchived eq false&skipScoping=true')
        ),
        api.getTemplateCountByTicket(),
        getCachedFeature(LICENSE_FEATURE.PermissionScoping),
      ]);

      const templateFeatureEnabled = result[0];
      setIsFeatureEnabled(templateFeatureEnabled);
      setScopingEnabled(result[3]);

      let templates = SortWithDefaultTemplateFirst(result[1].data.value);
      const templateCounts = result[2];
      const rowData: RowsData[] = templates.map((template: TicketTemplate) => setTemplate(template, templateCounts));
      setData(rowData);
    } catch (err: any) {
      console.error(err);
      setApiStatus({
        ...apiStatus,
        status: ERROR,
        errCode: (err.response) ? err.response.status : ""
      });
    } finally {
      setApiStatus({ ...apiStatus, status: DONE });
    }
  }, []);

  const loadPage = () => {
    fetchData();
  }

  const getMenuItems = (template: TicketTemplate) => {
    const items = [
      {
        content: "Edit",
        icon: <FontIcon iconName={"Edit"} className={`text-lg`} style={{ lineHeight: "unset" }} />,
        onClick: (e) => {
          e.stopPropagation();
          history.push(`/settings/templates?id=${template.Id}`);
        },
        hidden: false
      },
      {
        content: template.IsArchived ? TREE_LISTING_CONSTANTS.unArchive : TREE_LISTING_CONSTANTS.archive,
        icon: template.IsArchived ? <UnArchiveIcon className={`${Style.svgIcon}`} /> : <ArchiveIcon className={`${Style.svgIcon}`} />,
        onClick: (e) => {
          e.stopPropagation();
          setSelectedTemplate(template);
          setArchiveDialogOpen(true);
        },
        hidden: template.IsDefault
      },
      {
        content: TREE_LISTING_CONSTANTS.delete,
        icon: <FontIcon iconName={"Delete"} className={`text-lg`} style={{ lineHeight: "unset" }} />,
        onClick: (e) => {
          e.stopPropagation();
          setSelectedTemplate(template);
          setDeleteDialogOpen(true);
        },
        hidden: template.IsDefault
      }
    ];
    if (!template.IsArchived) {
      items.push({
        content: t('common.direct-link'),
        icon: <LinkIcon outline />,
        onClick: (e) => {
          e.stopPropagation();
          (async () => {
            await navigator.clipboard.writeText(`https://${window.location.host}/knowledge/template/${template.Id}`);
            toast.success(t('common.link-copied'))
          })();
        },
        hidden: false
      });
    }
    return items.filter(item => !item.hidden)
  }

  const setTemplate = (template: TicketTemplate, ticketCount: any[]) => {
    const assigneeKey = t('template.fields.assignee');
    const ticketCountKey = t('template.template-headers.ticket-count');
    const teamKey = t('template.fields.team');
    const groupKey = t('template.fields.group');
    const customFormExistsKey = t('template.template-headers.custom-forms-exist');
    const lifecycleKey = t('template.fields.lifecycle');
    const modifiedDateKey = t('template.template-headers.modified-date');
    const assigneeName = template.Assignee?.FullName ?? '';
    const teamName = template.Team?.Name ?? '';
    const groupName = template.SupportGroup?.Name ?? '';
    const lifecycleTitle = template.Lifecycle?.Title ?? '';
    const modifiedDateIso = template.ModifiedDate ?? '';
    const customFormExists = template.NoCustomForm ? t('common.buttons.no') : t('common.buttons.yes');
    const templateTicketCount = getTemplateTicketCount(template.Id, ticketCount);
    return {
      id: template.Id,
      nameSearch: template.Name,
      [templatesSortMeta.title]: template.Name ?? '',
      [templatesSortMeta.ticketCount]: templateTicketCount,
      [templatesSortMeta.assigneeSort]: assigneeName,
      [templatesSortMeta.teamSort]: teamName,
      [templatesSortMeta.groupSort]: groupName,
      [templatesSortMeta.customFormExistsSort]: template.NoCustomForm ? 0 : 1,
      [templatesSortMeta.lifecycleSort]: lifecycleTitle,
      [templatesSortMeta.modifiedDateIso]: modifiedDateIso,
      [t('template.template-headers.name')]: (
        <Flex gap="gap.medium" vAlign="center">
          <Text content={template.Name} />
          {template.IsArchived && (
            <Text content={TREE_LISTING_CONSTANTS.archived} size="smaller" weight="light" disabled />
          )}
        </Flex>
      ),
      [ticketCountKey]: <Text content={templateTicketCount.toString()} />,
      [teamKey]: <Text content={teamName} />,
      [groupKey]: <Text content={groupName} />,
      [assigneeKey]: <Text content={assigneeName} />,
      [customFormExistsKey]: <Text content={customFormExists} />,
      [ticketCountKey]: <Text content={templateTicketCount.toString()} />,
      [teamKey]: <Text content={teamName} />,
      [groupKey]: <Text content={groupName} />,
      [assigneeKey]: <Text content={assigneeName} />,
      [customFormExistsKey]: <Text content={customFormExists} />,
      [lifecycleKey]: <Text content={lifecycleTitle} />,
      [t('template.template-headers.created-date')]: DateTime.fromJSDate(new Date(template.CreatedDate)).toLocaleString(DateTime.DATETIME_SHORT),
      [t('scoping.available-to')]: <ScopePermissionPeopleComponent scopedMembers={template.ScopedMembers} maxWidth={SHARED_TO_COLUMN_WIDTH}/>,
      [TABLE_SORT_META.createdDateIso]: template.CreatedDate ?? '',
      [modifiedDateKey]: <Text content={template.ModifiedDate ? DateTime.fromJSDate(new Date(template.ModifiedDate)).toLocaleString(DateTime.DATETIME_SHORT) : ''} />,
      "": (
        <div className='flex items-center flex-row-reverse pr-7'>
          <CheckLicense disable>
          <MenuButton
            trigger={
                <Button icon={<MoreIcon />} text iconOnly title="Actions" onClick={(e) => {
                  e.stopPropagation();
                }} />
            }
            menu={{
              items: getMenuItems(template)
            }}
          />
          </CheckLicense>
        </div>
      )
    }
  };

  const deleteTemplate = async (temp: TicketTemplate) => {

    try {
      setData((currentRows: RowsData[]) => {
        const rows = currentRows.filter(x => x.id != temp.Id);
        return rows;
      });
      await api.deleteTicketTemplate(temp.Id);
    } catch (err) {
      setData((currentRows: RowsData[]) => {
        currentRows.push(setTemplate(temp, []));
        return currentRows;
      });
      toast.error(`There was an error deleting Template "${temp.Name}"`, toastDefault);
    } finally {
      toast.success(`Template "${temp.Name}" is deleted.`, toastDefault);
    }
  };

  const archiveTemplate = async (temp: TicketTemplate) => {
    try {
      const apiData: TicketTemplate = {
        ...temp,
        IsArchived: !temp.IsArchived,
      };
      delete apiData.CreatedBy;
      delete apiData.Assignee;
      delete apiData.Team;
      delete apiData.SupportGroup;
      delete apiData.Lifecycle;
      await api.updateTicketTemplate(apiData);
      fetchData();
    } catch (err: any) {
      setApiStatus({
        ...apiStatus,
        status: ERROR,
        errCode: (err.response) ? err.response.status : ""
      });
    }
  };

  const navs: Breadcrumbs = {
    breadcrumbs: [
      {
        title: toTitleCase(SETTINGS),
        link: `/${SETTINGS}`
      },
      {
        title: toTitleCase(TEMPLATES),
        link: ''
      }
    ]
  };

  const errConfig = {
    fields: {
      title:
        apiStatus.errCode === 401
          ? UNAUTHORIZED_ACCESS
          : SOMETHING_WENT_WRONG,
      desc: CONTACT_ADMIN
    }
  };

  return (
    <CheckLogin onSignedIn={loadPage}>
      <Helmet>
        <title>{toTitleCase(TEMPLATES)} - Tikit</title>
      </Helmet>
      <div className="hidden md:block">
        {
          apiStatus.status === LOADING ? (
            <LoadingScreen message={apiStatus.msg} />
          ) : apiStatus.status === ERROR ? (
            <>
              <Communication {...errConfig} />
              {apiStatus.errCode === 401 && (
                <Button
                  content={REFRESH_SESSION}
                  primary
                  onClick={e => {
                    Providers.globalProvider.login();
                  }}
                />
              )}
            </>
          ) : (
            <>
              <Dialog
                style={{ maxWidth: '500px' }}
                open={archiveDialogOpen}
                onCancel={() => setArchiveDialogOpen(false)}
                cancelButton={toTitleCase(BUTTONS.CANCEL)}
                confirmButton={
                  selectedTemplate?.IsArchived
                    ? TREE_LISTING_CONSTANTS.unArchive
                    : TREE_LISTING_CONSTANTS.archive
                }
                onConfirm={() => { setArchiveDialogOpen(false); archiveTemplate(selectedTemplate) }}
                content={`${(!selectedTemplate.IsArchived) ? ARCHIVE_CONFIRMATION_WARNING : UNARCHIVE_CONFIRMATION_SUBTITLE}`}
                header={`${(!selectedTemplate.IsArchived) ? ARCHIVE_MODAL_HEADER : UNARCHIVE_MODAL_HEADER} ‘${selectedTemplate?.Name}’?`}
              />
              {(!isFeatureEnabled) ? <UpgradeNeeded
                        toolbarText={`${toTitleCase(SETTINGS)} > ${toTitleCase(TEMPLATES)}`}
                        headerText={t('ticket.templates.no-access')}
                        subheaderText={t('ticket.templates.upgrade-plan')}/> : 
              <KBListWrapper
                title={toTitleCase(TEMPLATES)}
                addNewTitle={isFeatureEnabled ? toTitleCase(NEW_TEMPLATE) : ""}
                routeName={SETTINGS_PAGES.TEMPLATES}
                headers={TEMPLATE_HEADERS.filter(x=> scopingEnabled || (!scopingEnabled && x.id!="availableTo")).map(x=>x.title)}
                rows={data}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                breadcrumbs={navs}
                ifEmpty={{
                  header: t('template.there-are-no-templates-lets-create-one'),
                  subHeader:
                    t('template.get-started-by-clicking-the-new-template-button')
                }}
                archiveViewSwitch
                archiveViewSwitchState={JSON.parse(localStorage.getItem('isArchivedTicketTemplatesView') || 'false')}
                onArchiveSwitchChange={(isArchived: boolean) => {
                  localStorage.setItem('isArchivedTicketTemplatesView', JSON.stringify(isArchived));
                  fetchData()
                }}
                sortConfig={ticketTemplatesSortConfig}
                wrapperClassName={`${Style.templatesTableWrapper} ${scopingEnabled ? Style.withSharedTo : ''}`}
              />}
              <TicketTemplatesDialog
                open={openDialog}
                onCancel={() => setOpenDialog(false)}
              />
              <Dialog
                style={{ maxWidth: '500px' }}
                open={deleteDialogOpen}
                onCancel={() => setDeleteDialogOpen(false)}
                cancelButton={toTitleCase(BUTTONS.CANCEL)}
                confirmButton={toTitleCase(BUTTONS.DELETE)}
                onConfirm={() => { setDeleteDialogOpen(false); deleteTemplate(selectedTemplate) }}
                content={t('template.template-delete-message')}
                header={`${t('template.template-form-confirm-delete-header',{ formName: selectedTemplate?.Name})}`}
              />
            </>
          )
        }
      </div>
      <MobileSettingsView />
    </CheckLogin>

  );
};